import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React from 'react';
import { bool, func, node, number, oneOf, oneOfType, string } from 'prop-types';
import { Icon } from '@magalu/stereo-ui-icons';
import { Container, Title } from './item.styles';
import ItemDescription from './item-description';
import ItemContent from './item-content';
import Text from '../Text';

const ItemHeader = _ref => {
  let {
    centered,
    description,
    content,
    endIcon,
    fill,
    fontSize,
    hint,
    onClick,
    startIcon,
    textAlign,
    title,
    ellipsis,
    as,
    titleAs,
    justifyContent
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["centered", "description", "content", "endIcon", "fill", "fontSize", "hint", "onClick", "startIcon", "textAlign", "title", "ellipsis", "as", "titleAs", "justifyContent"]);

  const children = React.Children.toArray(props == null ? void 0 : props.children);

  const buildIcon = icon => icon && typeof icon === 'string' ? /*#__PURE__*/React.createElement(Icon, {
    name: icon,
    fill: fill,
    "data-testid": "item-icon"
  }) : icon;

  const buildHint = () => hint && typeof hint === 'string' ? /*#__PURE__*/React.createElement(Text, {
    as: "label",
    fontSize: 1,
    fontWeight: "normal",
    "data-testid": "item-hint"
  }, hint) : hint;

  const buildDescription = () => description && typeof description === 'string' ? /*#__PURE__*/React.createElement(ItemDescription, null, description) : description;

  const buildTitle = () => title && typeof title === 'string' ? /*#__PURE__*/React.createElement(Title, {
    fontSize: fontSize,
    textAlign: textAlign,
    centered: centered,
    "data-testid": "item-title",
    forwardedAs: titleAs
  }, title, buildDescription()) : title;

  const buildChildren = () => {
    const hasDescription = buildDescription();
    const ellipsisStyle = ellipsis ? {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      width: '100%'
    } : null;
    return hasDescription ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Title, {
      fontSize: fontSize,
      textAlign: textAlign,
      centered: centered,
      style: ellipsisStyle,
      "data-testid": "item-title",
      forwardedAs: titleAs
    }, children, hasDescription)) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Title, {
      fontSize: fontSize,
      textAlign: textAlign,
      centered: centered,
      style: ellipsisStyle,
      "data-testid": "item-title",
      forwardedAs: titleAs,
      justifyContent: justifyContent
    }, children == null ? void 0 : children.filter(({
      type: {
        displayName
      } = {}
    }) => displayName !== 'ItemContent')));
  };

  const buildContent = () => {
    const contentAsChild = children == null ? void 0 : children.find(({
      type: {
        displayName
      } = {}
    }) => displayName === 'ItemContent');
    if (contentAsChild) return contentAsChild;
    return content ? /*#__PURE__*/React.createElement(ItemContent, null, content) : null;
  };

  return /*#__PURE__*/React.createElement(Container, _extends({
    forwardedAs: as,
    centered: centered,
    textAlign: textAlign,
    onClick: onClick,
    "data-testid": "item",
    justifyContent: justifyContent
  }, props), buildIcon(startIcon), buildTitle() || buildChildren(), buildHint() || buildIcon(endIcon), buildContent());
};

process.env.NODE_ENV !== "production" ? ItemHeader.propTypes = {
  /**
   * The content of the component,
   */
  children: node,

  /**
   * The title displayed in the Item
   */
  title: node,

  /**
   * The title displayed in the Item
   */
  description: node,

  /**
   * The content displayed in the Item
   */
  content: node,

  /**
   * Element before after the children
   */
  hint: node,

  /**
   * Element before after the children
   */
  startIcon: node,

  /**
   * Element before after the children
   */
  endIcon: node,

  /**
   * The variant to use.
   */
  variant: oneOf(['primary', 'secondary', 'light', 'transparent']),

  /**
   * The scale to use
   */
  scale: oneOf(['small', 'medium', 'large']),

  /**
   * If `true`, the Items are flexibly started.
   * @default false
   */
  justifyContent: bool,

  /**
   * If `true`, the Items are centered.
   * @default false
   */
  centered: bool,

  /**
   * Text color based on theme
   * @default 'inherit' from theme
   */
  fontSize: oneOfType([number, string]),

  /**
   * Align text.
   * @default left
   */
  textAlign: oneOf(['left', 'center', 'right']),

  /**
   * Text color based on theme
   * @default 'inherit' from theme
   */
  color: string,

  /**
   * Icon color based on theme
   * @default 'currentColor' from theme
   */
  fill: string,

  /**
   * Element use ... on long texts
   */
  ellipsis: bool,

  /**
   * Space between Items, based on theme
   * @default 2
   */
  gap: oneOfType([number, string]),

  /**
   * Element before after the children
   */
  as: string,

  /**
   * Choice a tag element to use
   */
  titleAs: string,

  /**
   * Called when click over Item.
   *
   * @param {SyntheticEvent} event - React's original SyntheticEvent.
   */
  onClick: func
} : void 0;
ItemHeader.defaultProps = {
  title: '',
  children: null,
  justifyContent: 'space-between',
  startIcon: null,
  endIcon: null,
  hint: null,
  centered: false,
  fill: 'currentColor',
  ellipsis: false
};
export default ItemHeader;